import type { FirestoreSubmissionData } from '@confer/lib'
import type { FirebaseApp } from 'firebase/app'
import { doc, getDoc, getFirestore } from 'firebase/firestore'
import { Functions, getFunctions, httpsCallable } from 'firebase/functions'
import type {
  RewardCampaignActivity,
  RewardCampaignInput,
  RewardCampaignSummary,
  RewardCodeBatch,
  RewardCodeBatchInput,
  RewardCodePreview,
  RewardControlInput,
  RewardManagedCode,
  RewardManagedEntry,
  RewardPage,
} from '../../types/rewardManagement'

export interface RewardManagementService {
  campaigns(surveyName: string): Promise<RewardCampaignSummary[]>
  control(input: RewardControlInput): Promise<void>
  activity(input: RewardCampaignInput): Promise<RewardCampaignActivity>
  entries(input: RewardCampaignInput): Promise<RewardManagedEntry[]>
  codes(input: RewardCampaignInput): Promise<RewardManagedCode[]>
  preview(input: RewardCampaignInput, contents: string): Promise<RewardCodePreview>
  createBatch(input: RewardCodeBatchInput): Promise<RewardCodeBatch>
  downloadBatch(
    input: RewardCampaignInput,
    batchId: string,
  ): Promise<{ batch: RewardCodeBatch; codes: string[] }>
  resumeBatch(input: RewardCampaignInput, batchId: string): Promise<RewardCodeBatch>
  submission(surveyName: string, submissionId: string): Promise<FirestoreSubmissionData | null>
}

export class FirebaseRewardManagementService implements RewardManagementService {
  private functions: Functions

  constructor(private app: FirebaseApp) {
    // The database service configures this app's Functions/Firestore emulator connections first.
    this.functions = getFunctions(app)
  }

  private async call<Input, Output>(name: string, input: Input): Promise<Output> {
    return (await httpsCallable<Input, Output>(this.functions, name, { timeout: 125_000 })(input))
      .data
  }

  campaigns(surveyName: string) {
    return this.call<{ surveyName: string }, RewardCampaignSummary[]>('getRewardCampaigns', {
      surveyName,
    })
  }

  async control(input: RewardControlInput) {
    await this.call('manageRewardCampaign', input)
  }

  activity(input: RewardCampaignInput) {
    return this.call<RewardCampaignInput, RewardCampaignActivity>('getRewardActivity', input)
  }

  private async collect<T>(name: string, input: RewardCampaignInput): Promise<T[]> {
    const items: T[] = []
    let after: string | undefined
    do {
      const page = await this.call<RewardCampaignInput & { after?: string }, RewardPage<T>>(name, {
        ...input,
        ...(after ? { after } : {}),
      })
      items.push(...page.items)
      if (page.next && page.next === after)
        throw new Error('The reward list did not advance. Please refresh.')
      after = page.next
    } while (after)
    return items
  }

  entries(input: RewardCampaignInput) {
    return this.collect<RewardManagedEntry>('getRewardEntries', input)
  }

  codes(input: RewardCampaignInput) {
    return this.collect<RewardManagedCode>('getRewardCodes', input)
  }

  preview(input: RewardCampaignInput, contents: string) {
    return this.call<RewardCampaignInput & { contents: string }, RewardCodePreview>(
      'previewRewardCodeImport',
      { ...input, contents },
    )
  }

  createBatch(input: RewardCodeBatchInput) {
    return this.call<RewardCodeBatchInput, RewardCodeBatch>('createRewardCodeBatch', input)
  }

  downloadBatch(input: RewardCampaignInput, batchId: string) {
    return this.call<
      RewardCampaignInput & { batchId: string },
      { batch: RewardCodeBatch; codes: string[] }
    >('downloadRewardCodeBatch', { ...input, batchId })
  }

  resumeBatch(input: RewardCampaignInput, batchId: string) {
    return this.call<RewardCampaignInput & { batchId: string }, RewardCodeBatch>(
      'resumeRewardCodeBatch',
      { ...input, batchId },
    )
  }

  async submission(surveyName: string, submissionId: string) {
    const snapshot = await getDoc(
      doc(getFirestore(this.app), 'surveys', surveyName, 'submissions', submissionId),
    )
    return snapshot.exists() ? (snapshot.data() as FirestoreSubmissionData) : null
  }
}
